import React, { useEffect, useState, useCallback } from 'react'
import { CalendarDays, Loader2, Plus } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Sidebar } from '@/components/ui/sidebar'
import Header from '@/components/ui/header'
import BottomNav from '@/components/ui/bottom-nav'
import { getMyWorkspaces, type WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import CalendarView from '@/features/calendar/components/CalendarView'
import BookMeetingModal from '@/features/bookings/components/BookMeetingModal'

// Standalone top-level nav item (Sidebar/BottomNav) — not nested under
// Settings → Integrations, which only manages the connection itself.
const CalendarPage: React.FC = () => {
  const { currentTheme: t } = useTheme()
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [bookOpen, setBookOpen] = useState(false)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    getMyWorkspaces()
      .then((ws) => { if (ws[0]) setWorkspace(ws[0]) })
      .finally(() => setLoading(false))
  }, [])

  const handleBooked = useCallback(() => setRefreshToken((n) => n + 1), [])

  return (
    <div className="h-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: t.primary }}>
      <div className="flex flex-1 min-h-0 overflow-hidden pb-16 md:pb-0">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Header title="Calendar" icon={<CalendarDays size={20} />} />
          <main className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-6 md:px-10 py-6 md:py-8 [scrollbar-gutter:stable]">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
              </div>
            ) : workspace ? (
              <div className="flex flex-col gap-4">
                <CalendarView
                  workspaceId={workspace.id}
                  refreshToken={refreshToken}
                  bookButton={
                    <button
                      onClick={() => setBookOpen(true)}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md text-white shrink-0"
                      style={{ backgroundColor: t.accent }}
                    >
                      <Plus size={13} />
                      Book meeting
                    </button>
                  }
                />
                <BookMeetingModal
                  workspaceId={workspace.id}
                  open={bookOpen}
                  onOpenChange={setBookOpen}
                  onBooked={handleBooked}
                />
              </div>
            ) : (
              <p className="text-sm" style={{ color: t.mutedText }}>No workspace found.</p>
            )}
          </main>
        </div>
      </div>
      <BottomNav />
    </div>
  )
}

export default CalendarPage
